// Combos y Surtidos — la pantalla aparte de los productos (ticket 8).
//
// Charly: "los combos no deben cargarse como productos: son packs que contienen varios
// productos, no tienen stock sino que dependen del stock de sus componentes. Podemos medir
// los combos vendidos y no vendidos, pero no mezclar datos de combos y productos".
//
// Por eso acá NO hay cobertura, ni días de stock, ni valor de inventario: un combo no tiene
// nada de eso. Tiene otras dos cosas, que son las que se muestran: cuánto se vendió y cuántos
// se pueden armar hoy con lo que hay de sus componentes.
import { useState } from 'react';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { Badge, DataState, FilterTabs, Panel, Table, Thead, Tr, Td } from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { useCombos } from '@/lib/hooks';
import { cn } from '@/lib/cn';
import { formatNumber, formatShortDate } from '@shared/format';
import type { Tone } from '@/lib/status';
import type { ComboItem } from '@shared/types';

const COLUMNAS = [
  { key: 'exp', label: '' },
  { key: 'pack', label: 'Pack' },
  { key: 'tipo', label: 'Tipo' },
  { key: 'vendidos', label: 'Unidades Vendidas', align: 'right' as const, wrap: true },
  { key: 'armables', label: 'Se Pueden Armar', align: 'right' as const, wrap: true },
  { key: 'limita', label: 'Qué lo limita' },
  { key: 'ultima', label: 'Última Venta', align: 'right' as const, wrap: true },
];

export default function CombosPage() {
  const combos = useCombos();
  const [filtro, setFiltro] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [abierto, setAbierto] = useState<string | null>(null);

  return (
    <DataState query={combos}>
      {(data) => {
        const visibles = filtrar(data.items, filtro, busqueda);
        return (
          <>
            <PageHeader
              title="Combos y Surtidos"
              subtitle={
                data.total === 0
                  ? 'El catálogo del B2B no tiene combos cargados.'
                  : `${data.total} packs · ${formatNumber(data.unitsSold)} unidades vendidas${
                      data.rangeFrom ? ` entre ${formatShortDate(data.rangeFrom)} y ${formatShortDate(data.rangeTo!)}` : ''
                    }`
              }
            />

            <p className="max-w-3xl text-xs leading-relaxed text-ink-muted">
              Un combo es un pack de varios productos: <strong className="text-ink">no tiene stock propio</strong>,
              depende del stock de sus componentes. Por eso está separado del semáforo y no tiene
              cobertura ni valor de inventario. Su venta ya está repartida entre los productos que
              lo componen — acá se mide como pack, y las dos cifras no se suman.
            </p>

            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <FilterTabs
                items={[
                  { key: 'todos', label: 'Todos', count: data.total },
                  { key: 'con_venta', label: 'CON VENTA', count: data.conVenta, tone: 'success' as Tone },
                  { key: 'sin_venta', label: 'SIN VENTA', count: data.sinVenta, tone: 'neutral' as Tone },
                  { key: 'sin_comp', label: 'SIN COMPONENTES', count: data.sinComponentes, tone: 'warning' as Tone },
                ]}
                value={filtro}
                onChange={setFiltro}
              />
              <div className="relative min-w-[240px] flex-1 lg:max-w-sm">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
                <input
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Buscar combo por nombre o SKU…"
                  className="h-10 w-full rounded-xl border border-hairline bg-panel pl-10 pr-3 text-sm text-ink-strong placeholder:text-ink-faint focus:border-primary focus:outline-none"
                />
              </div>
            </div>

            {/* Un combo sin componentes cargados no es solo una fila incompleta: su venta no se
                puede repartir entre productos reales, así que esos productos quedan
                subestimados en el forecast. Se avisa arriba porque se arregla en el B2B. */}
            {data.sinComponentes > 0 && (
              <div className="surface border-l-2 border-l-warning p-3 text-xs leading-relaxed text-ink-muted">
                <strong className="text-ink-strong">
                  {data.sinComponentes === 1
                    ? '1 pack no tiene sus componentes cargados en el B2B.'
                    : `${data.sinComponentes} packs no tienen sus componentes cargados en el B2B.`}
                </strong>{' '}
                Mientras siga así no se puede saber cuántos se arman, y lo que se vendió de ellos
                no se le suma a la demanda de los productos que llevan adentro.
              </div>
            )}

            <Panel>
              {visibles.length === 0 ? (
                // "No coincide con el filtro" con el catálogo entero vacío culpa al filtro de
                // algo que no hizo, y manda a tocar controles que no van a cambiar nada. Son
                // dos situaciones distintas y llevan a hacer cosas distintas.
                <p className="mx-auto max-w-prosa px-5 py-10 text-center text-sm text-ink-muted">
                  {data.total === 0
                    ? 'El catálogo del B2B no trajo ningún combo ni surtido en la última corrida. Si esperabas ver alguno, apretá «Actualizar datos» en el Dashboard: los combos se separan del resto de los productos durante la ingesta.'
                    : 'Ningún pack coincide con el filtro.'}
                </p>
              ) : (
                <Table>
                  <Thead columns={COLUMNAS} />
                  <tbody>
                    {visibles.map((c) => (
                      <FilaCombo
                        key={c.sku}
                        combo={c}
                        abierto={abierto === c.sku}
                        onToggle={() => setAbierto((s) => (s === c.sku ? null : c.sku))}
                      />
                    ))}
                  </tbody>
                </Table>
              )}
            </Panel>
          </>
        );
      }}
    </DataState>
  );
}

function FilaCombo({ combo, abierto, onToggle }: { combo: ComboItem; abierto: boolean; onToggle: () => void }) {
  const cuello = combo.components.find((x) => x.sku === combo.bottleneck) ?? null;
  return (
    <>
      <Tr className={cn(abierto && 'bg-subtle')}>
        <Td>
          <button onClick={onToggle} className="text-ink-faint hover:text-ink-strong" aria-label="Ver componentes">
            {abierto ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </Td>
        <Td>
          <p className="font-medium text-ink-strong">{combo.name}</p>
          <p className="font-mono text-2xs text-ink-faint">{combo.sku}</p>
        </Td>
        <Td>
          <Badge tone={combo.kind === 'surtido' ? 'info' : 'violet'}>{combo.kind}</Badge>
        </Td>
        <Td align="right">
          {combo.unitsSold > 0 ? (
            <>
              <span className="font-semibold text-ink-strong">{formatNumber(combo.unitsSold)}</span>
              <span className="block text-2xs text-ink-faint">
                en {combo.daysWithSales} día{combo.daysWithSales === 1 ? '' : 's'}
              </span>
            </>
          ) : (
            <span className="text-2xs text-ink-faint">sin venta</span>
          )}
        </Td>
        <Td align="right">
          <Armables combo={combo} />
        </Td>
        <Td>
          {cuello ? (
            <>
              <p className="truncate text-xs text-ink">{cuello.name}</p>
              <p className="text-2xs text-ink-faint">
                {formatNumber(cuello.stock)} uds · {cuello.qty} por combo
              </p>
            </>
          ) : (
            <span className="text-2xs text-ink-faint">—</span>
          )}
        </Td>
        <Td align="right">
          <span className="text-2xs text-ink-muted">
            {combo.lastSale ? formatShortDate(combo.lastSale) : '—'}
          </span>
        </Td>
      </Tr>

      {abierto && (
        <tr className="border-b border-hairline/60 bg-subtle">
          <td />
          <td colSpan={COLUMNAS.length - 1} className="px-4 py-3">
            {combo.components.length === 0 ? (
              <p className="text-xs text-warning-text">
                Este pack no tiene componentes cargados en el B2B: no se sabe de qué está hecho.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {combo.components.map((k) => (
                  <li key={k.sku} className="flex items-center justify-between gap-4 text-xs">
                    <span className="min-w-0 truncate text-ink">
                      {k.name} <span className="font-mono text-2xs text-ink-faint">{k.sku}</span>
                    </span>
                    <span className="shrink-0 text-2xs text-ink-muted">
                      {k.qty} por combo · stock {formatNumber(k.stock)} ·{' '}
                      <span className={k.sku === combo.bottleneck ? 'font-semibold text-warning-text' : ''}>
                        alcanza para {formatNumber(k.buildable)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/** null = no se sabe de qué está hecho; 0 = se sabe y hoy no alcanza. No es lo mismo. */
function Armables({ combo }: { combo: ComboItem }) {
  if (combo.buildable == null) return <span className="text-2xs text-warning-text">sin componentes</span>;
  return (
    <>
      <span className={cn('font-semibold', combo.buildable === 0 ? 'text-danger-text' : 'text-ink-strong')}>
        {formatNumber(combo.buildable)}
      </span>
      <span className="block text-2xs text-ink-faint">con el stock de hoy</span>
    </>
  );
}

function filtrar(items: ComboItem[], filtro: string, busqueda: string): ComboItem[] {
  const q = busqueda.trim().toLowerCase();
  return items.filter((c) => {
    if (filtro === 'con_venta' && c.unitsSold === 0) return false;
    if (filtro === 'sin_venta' && c.unitsSold > 0) return false;
    if (filtro === 'sin_comp' && c.buildable !== null) return false;
    if (q && !`${c.sku} ${c.name} ${c.category} ${c.supplier}`.toLowerCase().includes(q)) return false;
    return true;
  });
}
